import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import omnicareLogo from "../assets/icons/logo.svg";
import LanguageSwitcher from "./LanguageSwitcher";
import "../styles/components/FeatureVotePage.css";

const FEATURES = [
  {
    id: "diet_analyzer",
    emoji: "🥗",
    titleKey: "featureVote.features.dietAnalyzer.title",
    titleFallback: "AI Diet Analyzer",
    descKey: "featureVote.features.dietAnalyzer.desc",
    descFallback:
      "Analyze your eating habits and get a personalized nutrition plan tailored to your health profile and risk factors.",
  },
  {
    id: "free_programs",
    emoji: "📍",
    titleKey: "featureVote.features.freePrograms.title",
    titleFallback: "Free Screening Finder",
    descKey: "featureVote.features.freePrograms.desc",
    descFallback:
      "Find and schedule free health screening programs in your area — we'll match you with available resources automatically.",
  },
  {
    id: "general_health",
    emoji: "🩺",
    titleKey: "featureVote.features.generalHealth.title",
    titleFallback: "General Health Module",
    descKey: "featureVote.features.generalHealth.desc",
    descFallback:
      "Track how blood pressure, cholesterol, and blood sugar affect your cancer risk and overall health over time.",
  },
];

const FeatureVotePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [votes, setVotes] = useState({});
  const [voteCounts, setVoteCounts] = useState({
    diet_analyzer: 0,
    free_programs: 0,
    general_health: 0,
  });

  // Load persisted votes from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("omnicare_feature_votes");
    if (saved) {
      try {
        setVotes(JSON.parse(saved));
      } catch {}
    }
    // TODO: fetch real vote counts from backend
    // For now, seed with plausible numbers
    setVoteCounts({
      diet_analyzer: 203,
      free_programs: 164,
      general_health: 127,
    });
  }, []);

  const toggleVote = (featureId) => {
    setVotes((prev) => {
      const updated = { ...prev, [featureId]: !prev[featureId] };
      localStorage.setItem("omnicare_feature_votes", JSON.stringify(updated));
      return updated;
    });
    setVoteCounts((prev) => ({
      ...prev,
      [featureId]: votes[featureId]
        ? prev[featureId] - 1
        : prev[featureId] + 1,
    }));
    // TODO: send vote to backend
  };

  const handleViewResults = () => {
    navigate("/results");
  };

  return (
    <div className="feature-vote-page">
      {/* Header */}
      <header className="survey-header">
        <div className="header-content">
          <div className="logo-section" onClick={() => navigate("/")}>
            <img src={omnicareLogo} alt="Omnicare" className="header-logo" />
            <span className="logo-text">Omnicare</span>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <div className="feature-vote-container">
        <div className="feature-vote-intro">
          <h1>
            {t(
              "featureVote.title",
              "🚀 What should we build next?"
            )}
          </h1>
          <p className="feature-vote-subtitle">
            {t(
              "featureVote.subtitle",
              "We're a non-profit building free tools for preventive health. Help us prioritize — vote for the features you want most."
            )}
          </p>
        </div>

        <div className="feature-cards">
          {FEATURES.map((feature) => {
            const isVoted = !!votes[feature.id];
            return (
              <div
                key={feature.id}
                className={`feature-card ${isVoted ? "voted" : ""}`}
              >
                <div className="feature-card-content">
                  <div className="feature-emoji">{feature.emoji}</div>
                  <h3>{t(feature.titleKey, feature.titleFallback)}</h3>
                  <p>{t(feature.descKey, feature.descFallback)}</p>
                </div>
                <button
                  className={`vote-button ${isVoted ? "voted" : ""}`}
                  onClick={() => toggleVote(feature.id)}
                >
                  <span className="vote-icon">{isVoted ? "❤️" : "🤍"}</span>
                  <span className="vote-count">
                    {voteCounts[feature.id] || 0}
                  </span>
                </button>
              </div>
            );
          })}
        </div>

        <div className="feature-vote-footer">
          <p className="feature-contact">
            {t(
              "featureVote.contact",
              "We're a non-profit — if you want to contribute or have questions about our results and evidence, reach out to"
            )}{" "}
            <a href="mailto:omnicareai@gmail.com">omnicareai@gmail.com</a>
          </p>
        </div>

        <div className="feature-vote-actions">
          <button
            className="view-results-button"
            onClick={handleViewResults}
          >
            {t("featureVote.viewResults", "View My Results →")}
          </button>
          <button
            className="view-results-v2-button"
            onClick={() => navigate("/results-v2")}
            style={{
              background: "linear-gradient(135deg, #064e3b, #1c1917)",
              color: "white",
              border: "none",
              padding: "14px 24px",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: "700",
              cursor: "pointer",
              width: "100%",
              maxWidth: "360px",
              margin: "8px auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            ✨ Try New Results Experience
          </button>
          <button
            className="skip-button"
            onClick={handleViewResults}
          >
            {t("featureVote.skip", "Skip")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeatureVotePage;
